import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Divider } from '@mui/material';
import { SystemIcon } from '@monorepo/nova-icons';
import {
  StyledScrollableContent,
  StyledScrollIndicator,
  StyledSymbolDescription,
  StyledSymbolInfo,
  StyledSymbolList,
  StyledSymbolName,
  StyledSymbolRow,
} from '../styled';
import { EnrichedSymbol } from '../types';

interface SupportedSymbolsListProps {
  symbols: EnrichedSymbol[];
}

export const SupportedSymbolsList: FC<SupportedSymbolsListProps> = ({
  symbols,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  const checkScroll = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 20;
    setShowScrollIndicator(!isAtBottom);
  }, []);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll);
    return () => el.removeEventListener('scroll', checkScroll);
  }, [checkScroll, symbols]);

  const handleScrollToBottom = useCallback(() => {
    contentRef.current?.scrollTo({
      top: contentRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, []);

  return (
    <StyledScrollableContent ref={contentRef}>
      <StyledSymbolList>
        {symbols.map((symbol, index) => (
          <React.Fragment key={symbol.code}>
            <StyledSymbolRow>
              <StyledSymbolInfo>
                <StyledSymbolName>{symbol.displayName}</StyledSymbolName>
                <StyledSymbolDescription>
                  {symbol.market}
                </StyledSymbolDescription>
              </StyledSymbolInfo>
              <SystemIcon iconName="CheckCircle" size={20} color="success" />
            </StyledSymbolRow>
            {index < symbols.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </StyledSymbolList>
      {showScrollIndicator && (
        <StyledScrollIndicator
          onClick={handleScrollToBottom}
          size="small"
          aria-label="Scroll to bottom"
        >
          <SystemIcon iconName="ArrowDown" size={20} />
        </StyledScrollIndicator>
      )}
    </StyledScrollableContent>
  );
};
